var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from 'classnames';
import React, { useRef, useState, useCallback } from 'react';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import Popover from 'react-bootstrap/lib/Popover';
import { useIntl } from 'react-intl';
import ItemsFilter from './filters/ItemsFilter';
import NumberFilter from './filters/NumberFilter';
import SearchFilter from './filters/SearchFilter';
import SelectFilter from './filters/SelectFilter';
import UserFilter from './filters/UserFilter';
import messages from './filters/filterMessages';
var MultiFilterColumnHeader = function (_a) {
    var filters = _a.filters, sortKey = _a.sortKey, onSort = _a.onSort, onFilterMultiple = _a.onFilterMultiple, _b = _a.searchParams, searchParams = _b === void 0 ? {} : _b, className = _a.className, title = _a.title, id = _a.id, _c = _a.visible, visible = _c === void 0 ? true : _c, style = _a.style, _d = _a.badge, badge = _d === void 0 ? undefined : _d, documentation = _a.documentation;
    var intl = useIntl();
    var target = useRef(null);
    var overlayTriggerRef = useRef(null);
    // State to track which filters have been changed by the user
    // We only send changed filters to avoid clearing unchanged ones
    var _e = useState({}), changedFilters = _e[0], setChangedFilters = _e[1];
    if (!visible)
        return null;
    // Check if any filter is currently active
    var hasActiveFilters = filters.some(function (f) {
        if (f.type === 'search') {
            return searchParams.search && searchParams.search[f.key] !== undefined;
        }
        if (f.type === 'select') {
            return searchParams.select && searchParams.select[f.key] !== undefined;
        }
        if (f.type === 'items' || f.type === 'user') {
            return (searchParams.filter &&
                searchParams.filter[f.key] &&
                searchParams.filter[f.key].items &&
                searchParams.filter[f.key].items.length > 0);
        }
        if (f.type === 'number') {
            return (searchParams.filter &&
                searchParams.filter[f.key] &&
                (searchParams.filter[f.key].lesser != null ||
                    searchParams.filter[f.key].greater != null));
        }
        return false;
    });
    // Handler for when a filter value changes
    var handleFilterChange = useCallback(function (key, value) {
        setChangedFilters(function (prev) {
            var _a;
            return (__assign(__assign({}, prev), (_a = {}, _a[key] = value, _a)));
        });
    }, []);
    var applyAllFilters = useCallback(function () {
        if (onFilterMultiple && Object.keys(changedFilters).length > 0) {
            var changes = Object.entries(changedFilters).map(function (_a) {
                var key = _a[0], value = _a[1];
                return ({
                    property: key,
                    filterRequest: value,
                });
            });
            onFilterMultiple(changes);
            setChangedFilters({});
        }
        if (overlayTriggerRef.current) {
            overlayTriggerRef.current.hide();
        }
    }, [onFilterMultiple, changedFilters]);
    var clearAllFilters = useCallback(function () {
        if (onFilterMultiple) {
            var changes = filters
                .filter(function (f) {
                if (f.type === 'number') {
                    return (searchParams.filter &&
                        searchParams.filter[f.key] &&
                        (searchParams.filter[f.key].lesser != null ||
                            searchParams.filter[f.key].greater != null));
                }
                return (searchParams.filter &&
                    searchParams.filter[f.key] &&
                    searchParams.filter[f.key].items &&
                    searchParams.filter[f.key].items.length > 0);
            })
                .map(function (f) {
                if (f.type === 'number') {
                    return { property: f.key, filterRequest: { lesser: null, greater: null } };
                }
                return { property: f.key, filterRequest: { items: [] } };
            });
            if (changes.length > 0) {
                onFilterMultiple(changes);
            }
            setChangedFilters({});
        }
        if (overlayTriggerRef.current) {
            overlayTriggerRef.current.hide();
        }
    }, [onFilterMultiple, filters, searchParams]);
    // Render a single filter component
    var getFilterComponent = function (filterConfig, index) {
        var isSearchFiltered = searchParams.search && searchParams.search[filterConfig.key] !== undefined;
        var isSelectFiltered = searchParams.select && searchParams.select[filterConfig.key] !== undefined;
        var isItemsFiltered = searchParams.filter &&
            searchParams.filter[filterConfig.key] &&
            searchParams.filter[filterConfig.key].items &&
            searchParams.filter[filterConfig.key].items.length > 0;
        var filterComponent;
        // All filters use the handleFilterChange callback
        // Individual filter buttons are hidden (hideFilterButton={true})
        var filterCallback = function (value) { return handleFilterChange(filterConfig.key, value); };
        switch (filterConfig.type) {
            case 'search':
                filterComponent = (_jsx(SearchFilter, { initialValue: searchParams.search && searchParams.search[filterConfig.key], onFilter: filterCallback, isFiltered: isSearchFiltered, intl: intl }));
                break;
            case 'items':
                filterComponent = (_jsx(ItemsFilter, { items: filterConfig.items || [], renderItem: filterConfig.renderItem, initialFilteredItems: searchParams.filter &&
                        searchParams.filter[filterConfig.key] &&
                        searchParams.filter[filterConfig.key].items, onFilter: filterCallback, isFiltered: isItemsFiltered, intl: intl, hideFilterButton: true }));
                break;
            case 'select':
                filterComponent = (_jsx(SelectFilter, { initialValue: searchParams.select && searchParams.select[filterConfig.key], onFilter: filterCallback, loadFunc: filterConfig.loadFunc, isFiltered: isSelectFiltered, intl: intl }));
                break;
            case 'user':
                filterComponent = (_jsx(UserFilter, { initialValue: searchParams.filter &&
                        searchParams.filter[filterConfig.key] &&
                        searchParams.filter[filterConfig.key].items &&
                        searchParams.filter[filterConfig.key].items[0], onFilter: filterCallback, isFiltered: isItemsFiltered, intl: intl, withUnassigned: filterConfig.withUnassigned !== false, hideFilterButton: true }));
                break;
            case 'number': {
                var isNumberFiltered = searchParams.filter &&
                    searchParams.filter[filterConfig.key] &&
                    (searchParams.filter[filterConfig.key].lesser != null ||
                        searchParams.filter[filterConfig.key].greater != null);
                filterComponent = (_jsx(NumberFilter, { initialValue: searchParams.filter && searchParams.filter[filterConfig.key], onFilter: filterCallback, isFiltered: isNumberFiltered, intl: intl, hideFilterButton: true }));
                break;
            }
            default:
                return null;
        }
        return (_jsxs(React.Fragment, { children: [index > 0 && (_jsx("div", __assign({ style: { marginTop: '10px' } }, { children: _jsx("hr", { style: { margin: '0px' }, className: "dark-line" }) }))), filterConfig.type === 'user' ? (_jsx("div", __assign({ style: { marginTop: '12px' } }, { children: filterComponent }))) : (filterComponent)] }, filterConfig.key));
    };
    var sortClassName = classNames('fa margin10R', {
        clickable: sortKey,
        'fa-caret-down': sortKey && searchParams.desc,
        'fa-caret-up': sortKey && !searchParams.desc,
    });
    return (_jsxs("th", __assign({ className: classNames(className, 'sticky-header'), id: id, style: style, ref: target }, { children: [_jsxs("span", __assign({ className: classNames({ clickable: onSort }), onClick: function () { return onSort && onSort(sortKey); } }, { children: [!!sortKey && sortKey === searchParams.sortKey && _jsx("i", { className: sortClassName }), title] })), badge && _jsx("br", {}), badge, filters.length > 0 && (_jsx(OverlayTrigger, __assign({ ref: overlayTriggerRef, trigger: "click", rootClose: true, placement: "bottom", overlay: _jsxs(Popover, __assign({ id: "multiFilterPopover-".concat(filters.map(function (f) { return f.key; }).join('-')) }, { children: [_jsx("button", __assign({ type: "button", className: "btn btn-inverse btn-block btn-sm margin10B", onClick: applyAllFilters }, { children: intl.formatMessage(messages.Filter) })), hasActiveFilters && (_jsxs("button", __assign({ type: "button", className: "btn btn-default btn-sm margin10B", onClick: clearAllFilters }, { children: [_jsx("i", { className: "fa fa-times" }), "\u00A0", _jsx("span", { children: intl.formatMessage(messages.ClearFilter) })] }))), filters.map(function (f, index) { return getFilterComponent(f, index); })] })), container: target.current }, { children: _jsx("i", { className: classNames('fa fa-filter margin10L clickable filter-icon', {
                        red: hasActiveFilters,
                    }) }) })))] })));
};
export default MultiFilterColumnHeader;
